import { AufnahmePunktDocument } from '../data-model';
import { auflageGesamt, auflageSummeKurz, gelaendeoberflaecheText } from './detail/hl-area/humus-auflage';
import { FilzKatalogZeile, FilzNachschlag, filzNachschlag, istFilzProfil } from './filz-bodenform';

/**
 * Filz: Tiefen ab Oberkante Ol, ohne die Rohdaten anzufassen.
 *
 * Die Regel (Nutzer-Entscheidung 24./25.09.2026, PLAN-filz-darstellung.md):
 *
 * - Eingegeben und gespeichert wird IMMER ab Oberkante Mineralboden, also ab
 *   der Unterkante der Auflage. Die Auflage sind drei Maechtigkeiten Ol, Of, Oh.
 * - Bei einem Filz (Filz-Bodenform ODER Auflage ueber 11 cm, siehe
 *   filz-bodenform.ts) gehoert die Auflage zum Profil: 0 cm liegt an der
 *   Oberkante des Ol. Alles unterhalb rueckt in der DARSTELLUNG um die
 *   Gesamt-Auflage nach unten - Tiefen der Horizonte und Substrate, die
 *   Kopfwerte (Karbonattiefe, Spiegeltiefe, Saugsaum), die Auswertungen mit
 *   festen Tiefengrenzen (nWSK bis 160 cm, Entwaesserungsgruppe), das PDF, das
 *   Grubenbild und die Exporte. Maechtigkeiten bleiben, wie sie sind.
 * - Das steht EINMAL hier. Keine Ausgabe rechnet selbst; sonst laufen die
 *   Fassungen auseinander, wie beim Rueckweg aus dem Offline-Modus und bei der
 *   Karten-Aufnahme geschehen.
 * - Und es wird gesagt: an der Eingabe (damit niemand bei einem Filz die
 *   Tiefen schon ab Ol eintraegt, die App verschoebe sie ein zweites Mal) und
 *   an jeder Ausgabe (damit niemand eine verschobene Zahl fuer die eingegebene
 *   haelt). Die Saetze stehen unten, ebenfalls einmal.
 *
 * Ob die Bodenform eine Filz-Bodenform ist, steht in der Feinbodenformen-Liste
 * der App. Die kommt aus der Datenbank und ist beim ersten Blick nicht immer
 * schon da; `registriereFilzKatalog` sagt, woher sie kommt, und der Nachschlag
 * wird gebaut, sobald sie da ist. Die Auflage-Regel braucht die Liste nicht.
 */
export interface FilzLage {
	/** Gehoert die Auflage zum Profil? */
	filz: boolean;
	/** Um so viel cm ruecken alle Tiefen unterhalb der Auflage nach unten; 0 ohne Filz. */
	versatz: number;
}

export const OHNE_FILZ: FilzLage = { filz: false, versatz: 0 };

let katalogQuelle: (() => FilzKatalogZeile[] | undefined) | undefined;
let nachschlag: FilzNachschlag | undefined;

/**
 * Sagt, woher die Feinbodenformen-Liste kommt (DataService, einmal beim Start
 * und nach jeder Aenderung der Listen). Der gebaute Nachschlag wird dabei
 * verworfen und beim naechsten Blick neu gebaut.
 */
export function registriereFilzKatalog(quelle: (() => FilzKatalogZeile[] | undefined) | undefined): void {
	katalogQuelle = quelle;
	nachschlag = undefined;
}

function aktuellerNachschlag(): FilzNachschlag | undefined {
	if (!nachschlag) {
		const zeilen = katalogQuelle?.() ?? [];
		if (!zeilen.length) return undefined;
		nachschlag = filzNachschlag(zeilen);
	}
	return nachschlag;
}

type PunktAusschnitt = Partial<Pick<AufnahmePunktDocument, 'header' | 'extendedHeader'>>;

/** Die Filz-Lage eines Punktes: ob, und um wie viel verschoben wird. */
export function filzLage(bp: PunktAusschnitt | null | undefined): FilzLage {
	const auflage = bp?.extendedHeader?.humusAuflage;
	const filz = istFilzProfil(bp?.header?.bodenform, auflage, aktuellerNachschlag());
	return filz ? { filz, versatz: auflageGesamt(auflage) } : OHNE_FILZ;
}

/** Nur der Versatz in cm; 0 ohne Filz. */
export function filzVersatz(bp: PunktAusschnitt | null | undefined): number {
	return filzLage(bp).versatz;
}

/**
 * Angezeigte Tiefe: eingegebene Tiefe plus Versatz. Ohne Tiefe keine Tiefe -
 * eine fehlende Eingabe wird nicht zu „12 cm".
 */
export function angezeigteTiefe(tiefe: number | null | undefined, versatz: number): number | undefined {
	if (typeof tiefe !== 'number' || !Number.isFinite(tiefe)) return undefined;
	return Math.round((tiefe + versatz + Number.EPSILON) * 100) / 100;
}

/**
 * Der Rueckweg fuer Auswertungen: Eine Grenze, die ab Oberkante Ol gilt (etwa
 * 160 cm fuer die nWSK), als eingegebene Tiefe - denn die Auswertungen rechnen
 * mit den Rohdaten. Nie unter 0: Eine Auflage, die dicker ist als die Grenze,
 * laesst vom Prueffenster nichts uebrig.
 */
export function eingegebeneTiefe(grenzeAbOl: number, versatz: number): number {
	return Math.max(0, grenzeAbOl - versatz);
}

/* --- Die Saetze dazu, einmal fuer alle Ausgaben -------------------------- */

function cm(versatz: number): string {
	return `${versatz.toString().replace('.', ',')} cm`;
}

/** Uebersicht, an der Zelle „organische Auflage · 12 cm". */
export function filzHinweisUebersicht(versatz: number): string {
	return `Filz: Tiefen ab Oberkante Ol, Eingabe ab Mineralboden (+${cm(versatz)})`;
}

/** Eingabemaske, an der Linie „Geländeoberfläche 0 cm". */
export function filzHinweisEingabe(versatz: number): string {
	return `Filz: Tiefen ab Mineralboden eingeben, angezeigt wird ab Oberkante Ol (+${cm(versatz)})`;
}

/** PDF, als Vermerk unter der Tabelle. */
export function filzHinweisPdf(versatz: number): string {
	return `Filz: Tiefen ab Oberkante Ol gerechnet (Auflage ${cm(versatz)}), eingegeben ab Oberkante Mineralboden.`;
}

/** Grubenbild, am Massband. */
export const FILZ_MASSBAND_TEXT = '0 cm = Oberkante Ol (Filz)';

/** Kurzprotokoll, unter Kalk- und Spiegeltiefe - ohne Filz. */
export const TIEFE_AB_MINERALBODEN_TEXT = 'Ab Oberkante Mineralboden gemessen.';

/**
 * Kurzprotokoll: unter Kalk- und Spiegeltiefe steht, ab wo gemessen wird. Bei
 * einem Filz mit Auflage gilt beides verschieden: Die Kalktiefe wird ab
 * Mineralboden eingegeben und ab Ol angezeigt, die Spiegeltiefe zaehlt ab Flur,
 * und Flur ist bei Filz die Oberkante Ol (Nutzer-Entscheidung 02.10.2026,
 * flur-tiefe.ts). Ein Filz OHNE erfasste Auflage verschiebt nichts, also auch
 * kein Hinweis darauf.
 */
export function kurzprotokollTiefenHinweis(bp: PunktAusschnitt | null | undefined): string {
	const lage = filzLage(bp);
	return lage.filz && lage.versatz > 0
		? `Filz: Kalktiefe ab Mineralboden eingeben, angezeigt wird ab Oberkante Ol (+${cm(lage.versatz)}). Spiegeltiefe ab Oberkante Ol.`
		: TIEFE_AB_MINERALBODEN_TEXT;
}

/**
 * Die Linie in der Eingabemaske: „Geländeoberfläche 0 cm · Auflage 12 cm", bei
 * Filz mit dem Hinweis, wie einzugeben ist.
 */
export function gelaendeoberflaecheZeile(bp: PunktAusschnitt | null | undefined): string {
	const text = gelaendeoberflaecheText(bp?.extendedHeader?.humusAuflage);
	const lage = filzLage(bp);
	return lage.filz ? `${text} · ${filzHinweisEingabe(lage.versatz)}` : text;
}

/**
 * Hilfe am (i) der Spalte „Tiefe bis" in der Eingabemaske. Sagt die Regel,
 * bevor jemand bei einem Filz die Tiefen ab Ol eintraegt.
 */
export function tiefeHilfetext(): string {
	return `Tiefe bis [cm]

Immer ab Oberkante Mineralboden eingeben, also ab der Unterkante der Auflage. Die Auflage (Ol, Of, Oh) steht als Mächtigkeit in den drei Zeilen darüber und zählt hier nicht mit.

Filz: Ist die Bodenform eine Filz-Bodenform oder die Auflage dicker als 11 cm, gehört die Auflage zum Profil, und 0 cm liegt an der Oberkante des Ol. Die Eingabe bleibt trotzdem ab Mineralboden. Übersicht, Protokoll, Grubenbild, Auswertungen (nWSK, Entwässerung, Karbonattiefe) und Exporte rechnen die Auflage von selbst dazu und sagen es dazu.`;
}

/**
 * Die Zelle der Substratseite in der Uebersicht: „organische Auflage · 12 cm",
 * bei Filz mit dem Hinweis, wie zu lesen ist.
 */
export function auflagePlatzhalterZeile(platzhalter: string, bp: PunktAusschnitt | null | undefined): string {
	const kurz = auflageSummeKurz(bp?.extendedHeader?.humusAuflage);
	const text = kurz ? `${platzhalter} · ${kurz}` : platzhalter;
	const lage = filzLage(bp);
	return lage.filz ? `${text} · ${filzHinweisUebersicht(lage.versatz)}` : text;
}
